import { useMemo } from "react";
import * as THREE from "three";
import { getFixingSpec } from "@/config/designer";
import type { DesignerEngine } from "@/core/engine/DesignerEngine";
import { resolveFixingOrientation } from "@/core/fixings/holeSolid";

const Z_AXIS = new THREE.Vector3(0, 0, 1);

interface FixingMarkersProps {
  engine: DesignerEngine;
  selectedId: string | null;
  visible: boolean;
  /** Re-read the engine whenever this changes. */
  revision: number;
}

/**
 * Selection rings for placed fixings. The hole itself is real geometry in the
 * viewport mesh; these only exist so a fixing can be picked up and moved.
 */
export function FixingMarkers({
  engine,
  selectedId,
  visible,
  revision,
}: FixingMarkersProps): React.ReactElement {
  const markers = useMemo(
    () =>
      engine.fixings.map((fixing) => {
        const spec = getFixingSpec(fixing.typeId);
        const orientation = resolveFixingOrientation(engine.field, spec, fixing.x, fixing.z);
        const surface = orientation?.surfacePoint ?? [fixing.x, 0, fixing.z];
        const axis = orientation?.axis ?? [0, 1, 0];
        const normal = new THREE.Vector3().fromArray(axis);
        return {
          id: fixing.id,
          position: new THREE.Vector3(
            surface[0] + axis[0] * 0.5,
            surface[1] + axis[1] * 0.5,
            surface[2] + axis[2] * 0.5,
          ),
          quaternion: new THREE.Quaternion().setFromUnitVectors(Z_AXIS, normal),
          radius: spec.countersinkDiameter / 2,
        };
      }),
    // `revision` is the engine's change signal — the fixing list itself is
    // mutable and deliberately not part of React state.
    [engine, revision],
  );

  if (!visible) return <group />;

  return (
    <group>
      {markers.map((marker) => {
        const selected = marker.id === selectedId;
        return (
          <mesh
            key={marker.id}
            position={marker.position}
            quaternion={marker.quaternion}
            renderOrder={5}
          >
            <ringGeometry args={[marker.radius + 1.1, marker.radius + 2.4, 48]} />
            <meshBasicMaterial
              color={selected ? "#f2f1e9" : "#c8d7bc"}
              transparent
              opacity={selected ? 1 : 0.7}
              side={THREE.DoubleSide}
              depthTest={false}
            />
          </mesh>
        );
      })}
    </group>
  );
}
